import React, { useState, useEffect } from "react";
import { Dropdown, IDropdownOption, MessageBar, MessageBarType } from "@fluentui/react";
import SaveButton from "@/ui-controls/SaveButton";
import CancelButton from "@/ui-controls/CancelButton";
import CustomerComboBox from "@/ui-controls/CustomerComboBox";
import { Grid } from "fluentui-react-grid";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  IntegrationDto,
  IntegrationTagDto,
  IntegrationTagMappingDto,
  listIntegrations,
  listIntegrationTags,
  listIntegrationTagMappings,
  setupCustomerDefaultIntegration,
} from "@/api/integrations";

interface Props {
  customerId?: string;
  onSave?: () => void;
  onClose?: () => void;
}

const SetDefaultForm = (props: Props) => {
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [tags, setTags] = useState<IntegrationTagDto[]>([]);
  const [integrations, setIntegrations] = useState<IntegrationDto[]>([]);
  const [tagMappings, setTagMappings] = useState<IntegrationTagMappingDto[]>([]);

  useEffect(() => {
    listIntegrationTags()
      .then((data) => setTags(data?.result || []))
      .catch(() => setTags([]));
    listIntegrations()
      .then((data) => setIntegrations(data?.result || []))
      .catch(() => setIntegrations([]));
    listIntegrationTagMappings(undefined, undefined, 1000)
      .then((data) => setTagMappings(data?.result || []))
      .catch(() => setTagMappings([]));
  }, []);

  const tagOptions: IDropdownOption[] = tags.map((t) => ({
    key: t.id,
    text: t.name,
  }));

  const formik = useFormik({
    initialValues: {
      customerId: props.customerId || "",
      integrationTagId: 0,
      integrationId: 0,
    },
    validationSchema: Yup.object({
      customerId: Yup.number()
        .required("Customer ID is required")
        .positive("Must be a positive number"),
      integrationTagId: Yup.number()
        .required("Category is required")
        .positive("Select a category"),
      integrationId: Yup.number()
        .required("Integration is required")
        .positive("Select an integration"),
    }),
    onSubmit: (values) => {
      setIsSaving(true);
      setErrorMessage("");
      setupCustomerDefaultIntegration({
        customerId: Number(values.customerId),
        integrationTagId: values.integrationTagId,
        integrationId: values.integrationId,
      })
        .then(
          () => {
            props.onSave?.();
          },
          (error) => {
            const data = error?.response?.data;
            if (data?.detail) {
              setErrorMessage(data.detail);
            } else {
              setErrorMessage("Failed to set default integration.");
            }
          }
        )
        .finally(() => setIsSaving(false));
    },
  });

  const integrationOptions: IDropdownOption[] = (() => {
    const selectedTagId = formik.values.integrationTagId;
    if (!selectedTagId) return [];
    const mappedIntegrationIds = tagMappings
      .filter((m) => m.integrationTagId === selectedTagId)
      .map((m) => m.integrationId);
    return integrations
      .filter((i) => mappedIntegrationIds.includes(i.id))
      .map((i) => ({ key: i.id, text: `${i.code} - ${i.name}` }));
  })();

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid dir="ltr">
        {errorMessage && (
          <Grid.Row>
            <Grid.Col sizeSm={12}>
              <MessageBar
                messageBarType={MessageBarType.error}
                onDismiss={() => setErrorMessage("")}
              >
                {errorMessage}
              </MessageBar>
            </Grid.Col>
          </Grid.Row>
        )}
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <CustomerComboBox
              selectedCustomerId={
                formik.values.customerId
                  ? Number(formik.values.customerId)
                  : undefined
              }
              onChange={(id) =>
                formik.setFieldValue("customerId", id ? String(id) : "")
              }
              disabled={isSaving}
              errorMessage={
                formik.errors.customerId && formik.touched.customerId
                  ? formik.errors.customerId
                  : undefined
              }
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <Dropdown
              label="Category"
              options={tagOptions}
              selectedKey={
                formik.values.integrationTagId || undefined
              }
              onChange={(_e, option) => {
                formik.setFieldValue(
                  "integrationTagId",
                  option ? (option.key as number) : 0
                );
                formik.setFieldValue("integrationId", 0);
              }}
              disabled={isSaving}
              placeholder="Select a category..."
              errorMessage={
                formik.touched.integrationTagId &&
                formik.errors.integrationTagId
                  ? formik.errors.integrationTagId
                  : undefined
              }
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row>
          <Grid.Col sizeSm={12}>
            <Dropdown
              label="Integration"
              options={integrationOptions}
              selectedKey={
                formik.values.integrationId || undefined
              }
              onChange={(_e, option) => {
                formik.setFieldValue(
                  "integrationId",
                  option ? (option.key as number) : 0
                );
              }}
              disabled={isSaving}
              placeholder="Select an integration..."
              errorMessage={
                formik.touched.integrationId &&
                formik.errors.integrationId
                  ? formik.errors.integrationId
                  : undefined
              }
            />
          </Grid.Col>
        </Grid.Row>
        <Grid.Row className="panel-buttons">
          <Grid.Col>
            <SaveButton
              text="Save"
              baseTypeProps={{ type: "submit", disabled: isSaving }}
            />
          </Grid.Col>
          <Grid.Col>
            <CancelButton
              text="Cancel"
              onClick={() => props.onClose?.()}
              baseTypeProps={{ disabled: isSaving }}
            />
          </Grid.Col>
        </Grid.Row>
      </Grid>
    </form>
  );
};

export default SetDefaultForm;
